import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Col, Row } from 'reactstrap';
import {
  AlertTriangle,
  ArrowLeft,
  Check,
  CheckCircle,
  Clock,
  Edit2,
  ExternalLink,
  FileText,
  Lock,
  MinusCircle,
  PauseCircle,
  Play,
  RotateCcw,
  Target,
  User,
  X,
  XCircle,
  Zap,
} from 'react-feather';
import { toast } from 'react-toastify';
import { Button, Card, DescriptionList, EmptyState, Field, Loader, Page, PageHeader, Select, TextInput } from '../../../ui';
import ClickToCall from '../../../Telephony/ClickToCall';
import AiSummaryCard from './AiSummaryCard';
import StudentDrawer, { StudentRef } from './StudentDrawer';
import {
  actorPayload,
  canPerformTransition,
  currentActor,
  customerLocalTime,
  effectiveTaskRoles,
  fetchAiSummaries,
  fetchTasksMe,
  FieldValues,
  FOLLOW_UP_ACTIVITY,
  isTasksLeaderRole,
  isTicketType,
  RM_TASKS_ENABLED,
  CallRecording,
  summarizeAnswers,
  summaryHasSubstance,
  TaskCall,
  TaskDetail,
  tasksApi,
  TaskTransitionCfg,
  TaskTypeCfg,
  TicketConfig,
  TicketResolution,
} from '../../../Tasks/tasksApi';
import { ActivityComposer, CapturedAnswers, ClosePicker, StageTrack } from './TicketPanel';
import { CountryFlag, LocalTimeDisplay, TaskIdChip } from './TaskActionModal';
import { Callout, IconButton, PriorityBadge, Segmented, TaskStatusBadge } from './ui-pending';
import './TaskDetailPage.scss';

interface AgentOption {
  externalId: string;
  name: string;
  team?: { key: string; name: string } | null;
}

const EVENT_ICONS: Record<string, string> = {
  CREATED: 'fa-plus-circle',
  STATUS_CHANGED: 'fa-exchange',
  ASSIGNED: 'fa-user',
  UNASSIGNED: 'fa-user-times',
  COMMENT: 'fa-pencil',
  CALL_LINKED: 'fa-phone',
  GOAL_MET: 'fa-check-circle',
  SYSTEM_NOTE: 'fa-info-circle',
  CALL_LOGGED: 'fa-mobile',
  FOLLOW_UP_SET: 'fa-clock-o',
  REOPENED: 'fa-undo',
  ACTIVITY_LOGGED: 'fa-check-square-o',
  CLOSED: 'fa-flag-checkered',
  MIGRATED: 'fa-random',
};

const fmt = (iso: string | null | undefined): string =>
  iso ? new Date(iso).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' }) : '—';

/** "3h ago" — for a glance, with the exact time still on hover. */
const ago = (iso: string | null | undefined): string => {
  if (!iso) return '';
  const mins = Math.round((Date.now() - Date.parse(iso)) / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.round(hrs / 24);
  return days === 1 ? 'yesterday' : `${days}d ago`;
};

const eventLine = (e: TaskDetail['events'][number]): string => {
  switch (e.type) {
    case 'CREATED':
      return `Task created${e.payload?.sourceEvent ? ` (${e.payload.sourceEvent})` : ''}`;
    case 'STATUS_CHANGED':
      return `${e.payload?.fromKey || '?'} → ${e.payload?.toKey || '?'}${
        e.payload?.reason ? ` (${e.payload.reason})` : ''
      }`;
    case 'ASSIGNED':
      return e.payload?.reassigned && e.payload?.fromName
        ? `Reassigned from ${e.payload.fromName} to ${e.payload?.name || 'agent'}`
        : `Assigned to ${e.payload?.name || e.payload?.assigneeExternalId || 'agent'}`;
    case 'UNASSIGNED':
      return e.payload?.fromName ? `Unassigned from ${e.payload.fromName}` : 'Unassigned';
    case 'COMMENT':
      return e.note || '';
    case 'CALL_LINKED':
      return `Call ${e.payload?.status || ''}${
        e.payload?.talkSeconds ? ` · ${Math.round(e.payload.talkSeconds / 60)}m talk` : ''
      }${e.payload?.disposition ? ` · ${e.payload.disposition}` : ''}`;
    case 'GOAL_MET':
      return `Goal verified: ${e.payload?.goalKey || ''}`;
    case 'CALL_LOGGED':
      return `Outreach: ${e.payload?.status || ''}`;
    case 'FOLLOW_UP_SET':
      return e.payload?.cleared
        ? 'Follow-up cleared'
        : `Follow-up set for ${fmt(e.payload?.followUpAt)}`;
    case 'REOPENED':
      return `Reopened: ${e.payload?.fromKey || '?'} → ${e.payload?.toKey || '?'}`;
    case 'ACTIVITY_LOGGED':
      return `${e.payload?.activityLabel || e.payload?.activityKey || 'Activity'}${
        e.payload?.followUpAt ? ` · follow-up ${fmt(e.payload.followUpAt)}` : ''
      }`;
    case 'CLOSED':
      return `${e.payload?.closedBy === 'SYSTEM' ? 'Closed by the system' : 'Closed'} — ${
        e.payload?.resolutionLabel || e.payload?.resolutionKey || '?'
      }${e.payload?.manualOverride ? ' (marked by agent — normally set by the system)' : ''}`;
    case 'MIGRATED':
      return e.note || 'Moved to the new ticket model';
    default:
      return e.note || e.type;
  }
};

/** Answer chips under an activity/close entry, e.g. "Reason: Personal › Travelling". */
const eventAnswers = (e: TaskDetail['events'][number], cfg: TicketConfig | null): string[] => {
  if (!cfg || !e.payload?.fieldValues || typeof e.payload.fieldValues !== 'object') return [];
  return summarizeAnswers(cfg, e.payload.fieldValues as FieldValues).map((l) => `${l.label}: ${l.value}`);
};

/**
 * Salesforce-style stage path: every happy-path status as a chevron —
 * green check = passed, highlighted = current, grey = ahead. A stage is
 * clickable when the workflow config allows a direct transition to it from
 * the current status; goal-gated stages show a lock until the system
 * verifies the goal.
 */
const WorkflowPath: React.FC<{
  task: TaskDetail;
  typeConfig: TaskTypeCfg;
  busy: boolean;
  myRoles: string[];
  onMove: (move: TaskTransitionCfg) => void;
}> = ({ task, typeConfig, busy, myRoles, onMove }) => {
  const path = typeConfig.statuses
    .filter((s) => s.isOnPath)
    .sort((a, b) => a.sortOrder - b.sortOrder);
  const currentIdx = path.findIndex((s) => s.key === task.status.key);
  const succeeded = task.resolution === 'SUCCESS';
  const failed = !!task.closedAt && !succeeded;

  const moveTo = (statusKey: string): TaskTransitionCfg | undefined =>
    typeConfig.transitions.find(
      (t) =>
        t.fromStatus.key === task.status.key &&
        t.toStatus.key === statusKey &&
        canPerformTransition(t.allowedRoles, myRoles),
    );

  return (
    <div className="td-path">
      <div className="td-path__track">
        {path.map((status, idx) => {
          const done = succeeded ? true : idx < currentIdx;
          const current = !succeeded && idx === currentIdx;
          const move = task.closedAt ? undefined : moveTo(status.key);
          const goalBlocked = !!move && move.requiresGoalMet && !task.goalMetAt;
          const clickable = !!move && !goalBlocked && !busy;
          const eventDriven =
            !move &&
            !done &&
            !current &&
            !task.closedAt &&
            typeConfig.transitions.some(
              (t) => t.fromStatus.key === task.status.key && t.toStatus.key === status.key,
            );
          const state = done ? 'done' : current ? 'current' : 'ahead';
          const cls = [
            'td-path__step',
            `td-path__step--${state}`,
            clickable && 'td-path__step--clickable',
            !done && !current && (goalBlocked || !move) && 'td-path__step--locked',
          ]
            .filter(Boolean)
            .join(' ');
          return (
            <div
              key={status.id}
              className={cls}
              role={clickable ? 'button' : undefined}
              tabIndex={clickable ? 0 : undefined}
              title={
                current
                  ? 'Current stage'
                  : goalBlocked
                    ? `Locked until the system verifies: ${task.taskType.goalDescription}`
                    : move
                      ? `Move to ${status.label}`
                      : done
                        ? 'Completed'
                        : eventDriven
                          ? 'Set automatically by the system (a team lead can override)'
                          : 'Not reachable from the current stage'
              }
              onClick={() => clickable && move && onMove(move)}
              onKeyDown={(e) => e.key === 'Enter' && clickable && move && onMove(move)}
            >
              {done && <Check size={13} aria-hidden />}
              {goalBlocked && <Lock size={13} aria-hidden />}
              {status.label}
            </div>
          );
        })}
      </div>
      {currentIdx === -1 && !task.closedAt && (
        <div className="td-path__note">
          <PauseCircle size={14} aria-hidden />
          <span>
            Currently parked in <strong>{task.status.label}</strong> — click a stage to resume.
          </span>
        </div>
      )}
      {failed && (
        <div className="td-path__note td-path__note--danger">
          <XCircle size={14} aria-hidden />
          <span>
            Closed as <strong>{task.status.label}</strong>
          </span>
        </div>
      )}
    </div>
  );
};

/**
 * Recording cell for a linked call: a lazy "Recording" button that fetches a
 * fresh (short-lived) playback URL + transcript from telephony on click, then
 * shows an inline player and a collapsible transcript.
 */
const RecordingCell: React.FC<{ call: TaskCall }> = ({ call }) => {
  const [rec, setRec] = useState<CallRecording | null>(null);
  const [loading, setLoading] = useState(false);
  const [showPlayer, setShowPlayer] = useState(false);
  const [showTranscript, setShowTranscript] = useState(false);

  if (!call.recordingAvailable && !call.recordingId) return <>—</>;

  // Fetch once (playback URL + transcript together); both buttons reuse it.
  const ensureLoaded = async (): Promise<CallRecording | null> => {
    if (rec) return rec;
    setLoading(true);
    try {
      const r = await tasksApi<CallRecording>(`/v1/tasks/calls/${call.id}/recording`);
      setRec(r);
      return r;
    } catch (err: any) {
      toast.error(err.message || 'Could not load recording');
      return null;
    } finally {
      setLoading(false);
    }
  };

  const togglePlayer = async (): Promise<void> => {
    if (showPlayer) return setShowPlayer(false);
    if (await ensureLoaded()) setShowPlayer(true);
  };
  const toggleTranscript = async (): Promise<void> => {
    if (showTranscript) return setShowTranscript(false);
    if (await ensureLoaded()) setShowTranscript(true);
  };

  return (
    <div className="td-recording">
      {/* Two independent controls */}
      <div className="rm-row">
        <Button
          size="sm"
          variant="secondary"
          className={showPlayer ? 'td-toggle--on' : undefined}
          aria-pressed={showPlayer}
          icon={<Play size={14} aria-hidden />}
          disabled={loading}
          onClick={togglePlayer}
        >
          Recording
        </Button>
        <Button
          size="sm"
          variant="secondary"
          className={showTranscript ? 'td-toggle--on' : undefined}
          aria-pressed={showTranscript}
          icon={<FileText size={14} aria-hidden />}
          disabled={loading}
          onClick={toggleTranscript}
        >
          Transcript
        </Button>
        {loading && <span className="td-spinner" role="status" aria-label="Loading recording" />}
      </div>

      {showPlayer && rec && (
        rec.playbackUrl ? (
          <audio controls preload="none" src={rec.playbackUrl} className="td-audio" />
        ) : (
          <div className="rm-caption">Recording still processing…</div>
        )
      )}

      {showTranscript && rec && (
        <div className="td-transcript">
          {rec.transcript
            || (rec.transcriptStatus
              ? `Transcript ${rec.transcriptStatus.toLowerCase()}…`
              : 'No transcript available.')}
        </div>
      )}
    </div>
  );
};

type TimelineFilter = '' | 'CALLS' | 'NOTES' | 'FOLLOWUPS';
type TimelineEvent = TaskDetail['events'][number];
const isFollowUpActivity = (e: TimelineEvent): boolean =>
  e.type === 'ACTIVITY_LOGGED' && e.payload?.activityKey === FOLLOW_UP_ACTIVITY;
const TIMELINE_FILTERS: { key: TimelineFilter; label: string; match: (e: TimelineEvent) => boolean }[] = [
  { key: '', label: 'All', match: () => true },
  {
    key: 'CALLS',
    label: 'Calls',
    match: (e) =>
      e.type === 'CALL_LINKED' || e.type === 'CALL_LOGGED' || (e.type === 'ACTIVITY_LOGGED' && !isFollowUpActivity(e)),
  },
  { key: 'NOTES', label: 'Notes', match: (e) => e.type === 'COMMENT' },
  { key: 'FOLLOWUPS', label: 'Follow-ups', match: (e) => e.type === 'FOLLOW_UP_SET' || isFollowUpActivity(e) },
];

interface TaskDetailViewProps {
  taskId: string;
  /** 'panel' = beside the task list (one column); 'page' = the routed page. */
  variant?: 'page' | 'panel';
  /** Called after any successful change, so a list beside it can refresh. */
  onChanged?: () => void;
  /** Panel only: close button. */
  onClose?: () => void;
}

/** Small uppercase section label, as in the Ticket Flow prototype. */
const SectionTitle: React.FC<{ children: React.ReactNode; right?: React.ReactNode }> = ({ children, right }) => (
  <div className="td-section-title">
    <span className="td-section-title__text">{children}</span>
    {right}
  </div>
);

/**
 * One task: header, details, stage, answers, actions and timeline. Used
 * both beside the task list (variant "panel") and as the routed page
 * (/pages/rm-tasks/:taskId). Controls come from the task type's config —
 * the status path + transitions for LEGACY types, activities + closing
 * reasons for TICKET types — so no workflow is hardcoded here.
 */
export const TaskDetailView: React.FC<TaskDetailViewProps> = ({ taskId, variant = 'page', onChanged, onClose }) => {
  const isPanel = variant === 'panel';
  const [task, setTask] = useState<TaskDetail | null>(null);
  const [allTypes, setAllTypes] = useState<TaskTypeCfg[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState('');
  const [declineReason, setDeclineReason] = useState('');
  const [followUpAt, setFollowUpAt] = useState('');
  const [timelineFilter, setTimelineFilter] = useState<TimelineFilter>('');
  // Re-render every 30s so the customer's local-time cell stays current.
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const timer = setInterval(() => setNow(new Date()), 30_000);
    return () => clearInterval(timer);
  }, []);

  const typeConfig = useMemo(
    () => (task ? allTypes.find((t) => t.key === task.taskType.key) : undefined),
    [allTypes, task],
  );
  // Types on the unified ticket model get the activity/close UI instead of
  // the status path; everything else on the page is shared.
  const ticketCfg: TicketConfig | null = isTicketType(typeConfig) ? typeConfig.ticketConfig : null;
  const answers = useMemo<FieldValues>(() => (task?.fieldValues ?? {}) as FieldValues, [task]);
  const closedTone = ticketCfg?.resolutions.find((r) => r.key === task?.resolutionKey)?.tone;

  /**
   * The last activity an RM logged: its label, when, and who. Read from the
   * timeline the page already has, so it costs no extra request.
   *
   * `lastActivityKey` carries the label; the newest ACTIVITY_LOGGED event
   * carries the when and the who. A ticket can hold the key without the
   * event (a system close, an older ticket), so the label stands alone if
   * the event isn't there.
   */
  const lastActivity = useMemo(() => {
    if (!ticketCfg || !task) return null;
    const label = task.lastActivityKey
      ? ticketCfg.activities.find((a) => a.key === task.lastActivityKey)?.label ?? task.lastActivityKey
      : null;
    if (!label) return null;
    const ev = (task.events ?? [])
      .filter((e) => e.type === 'ACTIVITY_LOGGED')
      .sort((a, b) => Date.parse(b.createdAt) - Date.parse(a.createdAt))[0];
    return { label, at: ev?.createdAt ?? null, by: ev?.actorName ?? null };
  }, [task, ticketCfg]);

  const me = useMemo(currentActor, []);
  // Leaders/admins may reassign the task between agents.
  const [isLeader, setIsLeader] = useState(false);
  const [agents, setAgents] = useState<AgentOption[]>([]);
  // The caller's effective roles, to gate transition controls exactly as the
  // backend does. Defaults to AGENT (least privilege) until /agents/me resolves.
  const [myRoles, setMyRoles] = useState<string[]>(['AGENT']);
  // The student's monthly AI summary. Looked up quietly when the ticket
  // opens so the button only appears when there's something worth reading —
  // most students have no support interactions in a given month, and a
  // button that always answers "nothing here" trains agents to ignore it.
  const [showSummary, setShowSummary] = useState(false);
  const [hasSummary, setHasSummary] = useState(false);
  // The student whose detail drawer is open (null = closed).
  const [drawerStudent, setDrawerStudent] = useState<StudentRef | null>(null);
  // Inline note editing: the COMMENT event being edited + its draft text.
  const [editingNoteId, setEditingNoteId] = useState<string | null>(null);
  const [noteDraft, setNoteDraft] = useState('');

  const saveNoteEdit = (eventId: string): void => {
    const text = noteDraft.trim();
    if (!text) {
      toast.warn('Note cannot be empty');
      return;
    }
    setEditingNoteId(null);
    mutate(() =>
      tasksApi(`/v1/tasks/${taskId}/comments/${eventId}`, {
        method: 'PATCH',
        body: JSON.stringify({ note: text, actor: actorPayload() }),
      }),
    );
  };

  // Refetched whenever the ticket changes, not once per page load.
  //
  // The config says which fields an activity asks and which it insists on, so
  // a stale copy is an RM being refused an answer their screen never offered.
  // Switching tickets does not remount this, so with `[]` an RM who opened
  // the queue in the morning kept the morning's rules all day — and a change
  // to a required field would have met them as an error they could not act
  // on without knowing to reload.
  useEffect(() => {
    tasksApi<TaskTypeCfg[]>('/v1/task-types')
      .then(setAllTypes)
      .catch(() => undefined);
  }, [taskId]);

  const load = useCallback(async () => {
    try {
      setTask(await tasksApi<TaskDetail>(`/v1/tasks/${taskId}`));
    } catch (err: any) {
      toast.error(err.message || 'Failed to load task');
    } finally {
      setLoading(false);
    }
  }, [taskId]);

  // Switching tickets in the panel: clear the previous one's state first.
  useEffect(() => {
    setTask(null);
    setLoading(true);
    setNote('');
    setDeclineReason('');
    setFollowUpAt('');
    setEditingNoteId(null);
    setTimelineFilter('');
    setShowSummary(false);
    setHasSummary(false);
  }, [taskId]);

  // Is there a summary worth offering? Cached per student, so flipping
  // between a student's tickets costs one request, not one per ticket.
  useEffect(() => {
    if (!task || task.customerType !== 'STUDENT') return undefined;
    let live = true;
    fetchAiSummaries(task.customerExternalId)
      .then((rows) => live && setHasSummary(rows.some(summaryHasSubstance)))
      .catch(() => undefined); // a missing summary is not worth an error
    return () => {
      live = false;
    };
  }, [task?.customerExternalId, task?.customerType]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const mine = await fetchTasksMe();
      if (cancelled) return;
      setMyRoles(effectiveTaskRoles(mine?.role));
      if (!isTasksLeaderRole(mine?.role)) return;
      setIsLeader(true);
      try {
        const list = await tasksApi<AgentOption[]>('/v1/agents');
        if (!cancelled) setAgents(list);
      } catch {
        /* leave picker empty on failure */
      }
    })();
    return () => { cancelled = true; };
  }, []);

  /** Leader/admin reassignment to any agent (or unassign with ''). */
  const reassign = (assigneeExternalId: string): void => {
    void mutate(() =>
      tasksApi(`/v1/tasks/${taskId}/assign`, {
        method: 'POST',
        body: JSON.stringify({
          assigneeExternalId: assigneeExternalId || undefined,
          actor: actorPayload(),
        }),
      }),
    );
  };

  /** Transitions an RM can trigger from the task's current status. */
  const moves: TaskTransitionCfg[] = useMemo(() => {
    if (!task || !typeConfig) return [];
    return typeConfig.transitions
      .filter((t) => t.fromStatus.key === task.status.key)
      .filter((t) => canPerformTransition(t.allowedRoles, myRoles))
      .sort((a, b) => a.toStatus.sortOrder - b.toStatus.sortOrder);
  }, [task, typeConfig, myRoles]);

  const mutate = async (fn: () => Promise<unknown>): Promise<void> => {
    setBusy(true);
    try {
      await fn();
      setNote('');
      setDeclineReason('');
      await load();
      onChanged?.();
    } catch (err: any) {
      toast.error(err.message || 'Action failed');
    } finally {
      setBusy(false);
    }
  };

  /** Like mutate, but tells the caller whether it worked (forms keep input on failure). */
  const run = async (fn: () => Promise<unknown>, success?: string): Promise<boolean> => {
    setBusy(true);
    try {
      await fn();
      if (success) toast.success(success);
      await load();
      onChanged?.();
      return true;
    } catch (err: any) {
      toast.error(err.message || 'Action failed');
      return false;
    } finally {
      setBusy(false);
    }
  };

  const logActivity = (input: {
    activityKey: string;
    fieldValues: FieldValues;
    followUpAt?: string;
    note?: string;
  }): Promise<boolean> =>
    run(
      () =>
        tasksApi(`/v1/tasks/${taskId}/activity`, {
          method: 'POST',
          body: JSON.stringify({ ...input, actor: actorPayload() }),
        }),
      'Activity logged',
    );

  const closeTicket = (input: { resolutionKey: string; fieldValues?: FieldValues; note?: string }): Promise<boolean> =>
    run(
      () =>
        tasksApi(`/v1/tasks/${taskId}/close`, {
          method: 'POST',
          body: JSON.stringify({ ...input, actor: actorPayload() }),
        }),
      'Ticket closed',
    );

  const needsDeclineReason = (move: TaskTransitionCfg): boolean =>
    move.toStatus.key === 'declined' && (typeConfig?.declinedReasons.length ?? 0) > 0;

  const transition = (move: TaskTransitionCfg): void => {
    if (move.requiresNote && !note.trim()) {
      toast.warn(`Moving to "${move.toStatus.label}" needs a note — add one below first`);
      return;
    }
    if (needsDeclineReason(move) && !declineReason) {
      toast.warn('Pick a decline reason first');
      return;
    }
    mutate(() =>
      tasksApi(`/v1/tasks/${taskId}/transition`, {
        method: 'POST',
        body: JSON.stringify({
          toStatusKey: move.toStatus.key,
          note: note.trim() || undefined,
          reason: needsDeclineReason(move) ? declineReason : undefined,
          actor: actorPayload(),
        }),
      }),
    );
  };

  /**
   * Undo a mistaken close — lands back on the status the task was in right
   * before the closing transition (the backend's default; no toStatusKey
   * sent). No role restriction on the backend, so no myRoles gate here either.
   */
  const reopen = (): void => {
    mutate(() =>
      tasksApi(`/v1/tasks/${taskId}/reopen`, {
        method: 'POST',
        body: JSON.stringify({ note: note.trim() || undefined, actor: actorPayload() }),
      }),
    );
  };

  const logCallStatus = (status: string): void => {
    mutate(() =>
      tasksApi(`/v1/tasks/${taskId}/call-status`, {
        method: 'POST',
        body: JSON.stringify({ status, note: note.trim() || undefined, actor: actorPayload() }),
      }),
    );
  };

  const saveFollowUp = (clear: boolean): void => {
    if (!clear && !followUpAt) {
      toast.warn('Pick the follow-up date & time first');
      return;
    }
    mutate(() =>
      tasksApi(`/v1/tasks/${taskId}/follow-up`, {
        method: 'POST',
        body: JSON.stringify({
          at: clear ? null : new Date(followUpAt).toISOString(),
          note: note.trim() || undefined,
          actor: actorPayload(),
        }),
      }),
    );
  };

  /**
   * Assign to me, provisioning first every time: the upsert is idempotent and
   * keeps the agent's display name in sync with the CMS profile (it never
   * touches role/isActive), so a stale "email as name" row heals itself.
   */
  const assignToMe = (): void => {
    mutate(async () => {
      if (me.externalId && me.email) {
        await tasksApi('/v1/agents', {
          method: 'POST',
          body: JSON.stringify({
            externalId: me.externalId,
            name: me.name || me.email,
            email: me.email,
            teamKey: task?.team?.key || 'cx',
          }),
        });
      }
      await tasksApi(`/v1/tasks/${taskId}/assign`, {
        method: 'POST',
        body: JSON.stringify({ assigneeExternalId: me.externalId, actor: actorPayload() }),
      });
    });
  };

  const comment = (): void => {
    if (!note.trim()) {
      toast.warn('Write the comment first');
      return;
    }
    mutate(() =>
      tasksApi(`/v1/tasks/${taskId}/comments`, {
        method: 'POST',
        body: JSON.stringify({ note: note.trim(), actor: actorPayload() }),
      }),
    );
  };

  if (loading || !task) {
    return <Loader label="Loading…" />;
  }

  // The sheet's SLA is "action it within N hours": a worked ticket isn't
  // overdue however long it stays open.
  const overdue = !!task.dueAt && !task.firstResponseAt && new Date(task.dueAt) < new Date();
  const slaMissed =
    !!task.firstResponseAt && !!task.dueAt && new Date(task.firstResponseAt) > new Date(task.dueAt);
  const actionSpan = (() => {
    if (!task.firstResponseAt) return '';
    const mins = Math.max(
      0,
      Math.round((new Date(task.firstResponseAt).getTime() - new Date(task.createdAt).getTime()) / 60_000),
    );
    const h = Math.floor(mins / 60);
    return h > 48 ? `${Math.floor(h / 24)}d` : h ? `${h}h ${mins % 60}m` : `${mins}m`;
  })();
  // Second SLA from the sheet ("Resolution Time"): how long it may stay open.
  const resolveDue = task.resolveDueAt ? new Date(task.resolveDueAt) : null;
  const resolutionLate = !!resolveDue && new Date(task.closedAt ?? Date.now()) > resolveDue;
  const local = customerLocalTime(task, now);

  /* ── Header: who, what, where it stands ─────────────────────────────── */
  const header = (
    <div className="td-header">
      <div className="td-header__who">
        <div className="rm-row td-header__meta">
          <TaskIdChip taskNumber={task.taskNumber} uuid={task.id} />
          <span className="ds-text-muted">{task.taskType.name}</span>
          <PriorityBadge priority={task.priority} />
        </div>
        <div className="rm-row td-header__name-row">
          <span className={`td-name${isPanel ? ' td-name--panel' : ''}`}>
            <CountryFlag phone={task.customerPhoneE164} />
            {task.customerType === 'STUDENT' ? (
              <button
                type="button"
                className="td-name__link"
                title="View profile details"
                onClick={() =>
                  setDrawerStudent({
                    studentExternalId: task.customerExternalId,
                    name: task.customerName,
                    phone: task.customerPhoneE164,
                    email: task.customerEmail,
                  })
                }
              >
                {task.customerName}
              </button>
            ) : (
              task.customerName
            )}
          </span>
          <ClickToCall
            inline
            hideNumber
            size="lg"
            number={task.customerPhoneE164}
            name={task.customerName}
            sourceRecordId={task.id}
            source="admin"
            fallbackToDialer
          />
        </div>
        {task.customerPhoneE164 && <div className="rm-caption">{task.customerPhoneE164}</div>}
      </div>
      <div className="td-header__actions">
        {hasSummary && (
          <button
            type="button"
            className={`td-ai-btn${showSummary ? ' td-ai-btn--on' : ''}`}
            aria-pressed={showSummary}
            title="What this student's support history says — from their monthly AI summary"
            onClick={() => setShowSummary((v) => !v)}
          >
            <Zap size={13} aria-hidden />
            AI summary
          </button>
        )}
        <TaskStatusBadge status={task.status} />
        {isPanel && (
          <a
            href={`${process.env.PUBLIC_URL}/pages/rm-tasks/${task.id}`}
            target="_blank"
            rel="noopener noreferrer"
            className="rm-icon-btn"
            title="Open in a new tab"
            aria-label="Open in a new tab"
          >
            <ExternalLink size={16} aria-hidden />
          </a>
        )}
        {isPanel && onClose && (
          <IconButton title="Close" aria-label="Close" onClick={onClose}>
            <X size={18} aria-hidden />
          </IconButton>
        )}
      </div>
    </div>
  );

  /* ── Act-now line: can I call, how urgent, when to follow up ────────── */
  const dot = <span className="td-facts__dot" aria-hidden="true">·</span>;
  const facts = (
    <div className="td-facts">
      {local ? <LocalTimeDisplay local={local} inline /> : <span className="ds-text-muted">Local time unknown</span>}
      {dot}
      {task.firstResponseAt ? (
        <span
          className={`td-facts__item ${slaMissed ? 'td-facts__item--late' : 'td-facts__item--ok'}`}
          title={`SLA was ${fmt(task.dueAt)} · first worked ${fmt(task.firstResponseAt)}`}
        >
          {slaMissed ? <Clock size={13} aria-hidden /> : <Check size={13} aria-hidden />}
          <span>
            Actioned in <strong>{actionSpan}</strong>
            {slaMissed && ' (past SLA)'}
          </span>
        </span>
      ) : (
        <span className={`td-facts__item${overdue ? ' td-facts__item--overdue' : ''}`} title="SLA — action it by this time. Counted in the assigned RM's working hours, so off-shift time doesn't count against it.">
          {overdue && <AlertTriangle size={13} aria-hidden />}
          <span>
            Action by <strong>{fmt(task.dueAt)}</strong>
          </span>
        </span>
      )}
      {resolveDue && !task.closedAt && (
        <>
          {dot}
          <span
            className={`td-facts__item${resolutionLate ? ' td-facts__item--late' : ''}`}
            title="Resolution Time — the sheet's second SLA: how long this ticket may stay open. Counted in the RM's working days, so week-offs don't eat it, and it always falls at the end of a shift."
          >
            <span>
              {resolutionLate ? 'Resolution overdue since ' : 'Resolve by '}
              <strong>{fmt(task.resolveDueAt)}</strong>
            </span>
          </span>
        </>
      )}
      {resolveDue && task.closedAt && resolutionLate && (
        <>
          {dot}
          <span className="td-facts__item td-facts__item--late" title={`Resolution SLA was ${fmt(task.resolveDueAt)}`}>
            Closed past resolution SLA
          </span>
        </>
      )}
      {dot}
      <span className="td-facts__item" title={task.followUpNote || 'Follow-up'}>
        <Clock size={13} aria-hidden className={task.followUpAt ? 'td-facts__icon--on' : undefined} />
        {task.followUpAt ? (
          <>
            <span>
              Follow-up <strong>{fmt(task.followUpAt)}</strong>
            </span>
            {!task.closedAt && (
              <IconButton
                className="td-icon-inline td-icon-inline--danger"
                title="Clear follow-up"
                aria-label="Clear follow-up"
                onClick={() => saveFollowUp(true)}
              >
                <X size={14} aria-hidden />
              </IconButton>
            )}
          </>
        ) : (
          <span className="ds-text-muted">No follow-up</span>
        )}
      </span>
    </div>
  );

  /* ── Why this ticket exists ───────────────────────────────────────────
     The producer sends the facts that caused the ticket in `context`, and
     until now none of it reached the RM. For a renewal that is the whole
     point: a learner with four courses gets a ticket about *one* of them,
     and without the course name and expiry the RM is calling blind — which
     is exactly what happened on day one.

     Shown as named fields, never as raw JSON. Keys the RM has no use for
     (ids, plumbing, our own assignment record) are held back behind a
     toggle rather than dropped, so nothing is unreachable when someone is
     debugging. */
  const CONTEXT_LABELS: Record<string, string> = {
    courseName: 'Course', levels: 'Level', expiryDate: 'Expires',
    classesLeft: 'Classes left', planClasses: 'Plan size',
    totalClassesPurchased: 'Classes purchased', scheduledClasses: 'Scheduled',
    completedClasses: 'Completed', lastPlan: 'Last plan',
    transactionType: 'Transaction', totalPrice: 'Amount', currency: 'Currency',
    invoiceDate: 'Invoice date', paymentFor: 'Payment for',
    packageType: 'Package', paymentMode: 'Payment mode', startDate: 'Start date',
    enrollmentId: 'Enrolment', courseId: 'Course id', orderId: 'Order',
    isOffline: 'Offline', isFree: 'Free', platform: 'Platform', source: 'Source',
    teacherName: 'Teacher', teacherExternalId: 'Teacher id',
  };
  /* Plumbing: true of every ticket, useful to nobody on a call. */
  const CONTEXT_HIDDEN = new Set([
    'env', 'rmAssignment', 'followOnFrom', 'timezone', 'couponCode',
    'invoiceNumber', 'paymentRecordId', 'razorpayOrderId', 'razorpayPaymentId',
  ]);
  /* The ones that answer "which course, and when does it lapse" — plus who
     the teacher is, which the teacher resignation and termination tickets are
     entirely about and which otherwise sat folded away under More detail. */
  const CONTEXT_PRIMARY = ['courseName', 'teacherName', 'levels', 'expiryDate', 'classesLeft',
                           'planClasses', 'totalClassesPurchased', 'startDate'];

  const ctx = (task.context ?? {}) as Record<string, unknown>;
  const ctxText = (v: unknown): string =>
    typeof v === 'boolean' ? (v ? 'Yes' : 'No') : String(v);
  const ctxEntries = Object.entries(ctx).filter(
    ([k, v]) => !CONTEXT_HIDDEN.has(k) && v !== null && v !== undefined && v !== '',
  );
  const primary = CONTEXT_PRIMARY
    .filter((k) => ctxEntries.some(([ek]) => ek === k))
    .map((k) => [k, ctx[k]] as [string, unknown]);
  const secondary = ctxEntries.filter(([k]) => !CONTEXT_PRIMARY.includes(k));

  const contextPanel = ctxEntries.length > 0 && (
    <div className="td-context">
      <div className="td-context__title">Why this ticket exists</div>
      {primary.length > 0 && (
        <DescriptionList items={primary.map(([k, v]) => ({ label: CONTEXT_LABELS[k] ?? k, value: ctxText(v) }))} />
      )}
      {secondary.length > 0 && (
        <details className="td-context__more">
          <summary>More detail ({secondary.length})</summary>
          <DescriptionList items={secondary.map(([k, v]) => ({ label: CONTEXT_LABELS[k] ?? k, value: ctxText(v) }))} />
        </details>
      )}
    </div>
  );

  /* ── Owner line: only when it tells someone something ─────────────────
     An agent on their own ticket already knows it's theirs; leads (who
     reassign), unassigned tickets (Assign to me) and someone else's ticket
     are when assignee/team matter. */
  const mine = !!task.assignee && task.assignee.externalId === me.externalId;
  const showOwner = isLeader || !task.assignee || !mine;
  const owner = showOwner && (
    <div className="td-owner">
      <User size={14} aria-hidden />
      {task.assignee ? (
        <span>
          <strong className="td-owner__name">{task.assignee.name}</strong>
          {task.team && <> · {task.team.name}</>}
        </span>
      ) : (
        <em>Unassigned{task.team && ` · ${task.team.name}`}</em>
      )}
      {!mine && !task.closedAt && (
        <Button size="sm" variant="secondary" disabled={busy} onClick={assignToMe}>
          Assign to me
        </Button>
      )}
      {isLeader && !task.closedAt && agents.length > 0 && (
        <Select
          size="sm"
          className="td-owner__select"
          disabled={busy}
          value={task.assignee?.externalId ?? ''}
          onChange={(e) => reassign(e.target.value)}
          title="Reassign this task"
          aria-label="Reassign this task"
          options={[
            { value: '', label: 'Unassigned' },
            ...agents.map((a) => ({ value: a.externalId, label: `${a.name}${a.team ? ` · ${a.team.name}` : ''}` })),
          ]}
        />
      )}
    </div>
  );

  /* ── Goal: what "done" means, one line under the stage ─────────────── */
  const goal = (
    <div className="td-goal">
      <Target size={14} aria-hidden className={task.goalMetAt ? 'td-goal__icon--met' : 'td-goal__icon'} />
      <span>
        Goal: {task.taskType.goalDescription}{' '}
        {task.goalMetAt ? (
          <span className="td-goal__met">✓ verified {fmt(task.goalMetAt)}</span>
        ) : (
          <span>— awaiting system verification</span>
        )}
      </span>
    </div>
  );

  /* ── Stage: the three-step track (ticket model) or the status path ──── */
  const stage = ticketCfg && task.stage ? (
    <StageTrack stage={task.stage} closedLabel={task.status.label} tone={closedTone} />
  ) : (
    <>
      {typeConfig && (
        <WorkflowPath task={task} typeConfig={typeConfig} busy={busy} myRoles={myRoles} onMove={transition} />
      )}
      {/* Off-path moves (Unreachable, Escalated, Declined…) — status changes too,
          just not on the happy path. */}
      {!task.closedAt && moves.some((m) => !m.toStatus.isOnPath) && (
        <div className="td-moves">
          {moves
            .filter((m) => !m.toStatus.isOnPath)
            .map((move) => (
              <Button
                key={move.id}
                size="sm"
                variant="secondary"
                className={move.toStatus.isTerminal ? 'td-move--terminal' : undefined}
                disabled={busy}
                title={move.requiresNote ? 'Requires a note (add below)' : undefined}
                onClick={() => transition(move)}
              >
                {move.toStatus.label}
                {move.requiresNote && <Edit2 size={11} aria-hidden />}
              </Button>
            ))}
          {(typeConfig?.declinedReasons ?? []).length > 0 && (
            <Select
              size="sm"
              className="td-moves__reason"
              value={declineReason}
              onChange={(e) => setDeclineReason(e.target.value)}
              title="Required when moving to Declined"
              aria-label="Decline reason"
              options={[
                { value: '', label: 'Decline reason…' },
                ...typeConfig!.declinedReasons.map((r) => ({ value: r, label: r })),
              ]}
            />
          )}
        </div>
      )}
    </>
  );

  /* ── Closed: outcome-coloured, one line, Reopen (no role gate) ────── */
  const outcome: TicketResolution['tone'] =
    closedTone ?? (task.resolution === 'SUCCESS' ? 'success' : task.resolution === 'CANCELLED' ? 'neutral' : 'fail');
  const OUTCOME_TONE = { success: 'success', fail: 'danger', neutral: 'neutral' } as const;
  const OutcomeIcon = outcome === 'success' ? CheckCircle : outcome === 'fail' ? XCircle : MinusCircle;
  const closedBlock = task.closedAt && (
    <Callout
      tone={OUTCOME_TONE[outcome]}
      className="td-closed"
      action={
        <Button
          size="sm"
          variant="ghost"
          icon={<RotateCcw size={14} aria-hidden />}
          disabled={busy}
          onClick={reopen}
          title="Picked the wrong one? Reopen — answers already captured are kept"
        >
          Reopen
        </Button>
      }
    >
      <span className="td-closed__line">
        <OutcomeIcon size={14} aria-hidden />
        <span>
          Closed as <strong>{task.status.label}</strong> · {fmt(task.closedAt)}
        </span>
      </span>
    </Callout>
  );

  /* ── Actions ─────────────────────────────────────────────────────────── */
  const actions = (
    <>
      {ticketCfg && !task.closedAt && (
        <>
          <div className="td-block">
            {/* Where the ticket stands: its own line, first, on the left.
                A lead opening an In Progress ticket saw an empty "Select
                activity…" and read it as nothing having happened. It sat on
                the right of the heading at first and was missed there —
                people read down the left edge, so that is where it goes.
                Not inside the dropdown: that control is an input for the
                next action, and putting history in it hides the difference
                between what happened and what is about to be logged, and
                invites logging the same activity twice. */}
            <div title={lastActivity?.at ? fmt(lastActivity.at) : undefined} className="td-last">
              {lastActivity ? (
                <>
                  Last activity: <b className="td-last__label">{lastActivity.label}</b>
                  {lastActivity.at ? ` · ${ago(lastActivity.at)}` : ''}
                  {lastActivity.by ? ` · ${lastActivity.by}` : ''}
                </>
              ) : (
                <em>Nothing logged on this ticket yet</em>
              )}
            </div>
            <SectionTitle>Log activity</SectionTitle>
            <ActivityComposer cfg={ticketCfg} answers={answers} busy={busy} onLog={logActivity} />
          </div>
          <div className="td-block">
            <SectionTitle>Close ticket</SectionTitle>
            <ClosePicker cfg={ticketCfg} answers={answers} busy={busy} onClose={closeTicket} />
          </div>
        </>
      )}
      {!ticketCfg && !task.closedAt && (typeConfig?.callStatuses ?? []).length > 0 && (
        <div className="td-block">
          <SectionTitle>Log outreach</SectionTitle>
          <div className="rm-row">
            {typeConfig!.callStatuses.map((cs) => (
              <Button key={cs} size="sm" variant="secondary" disabled={busy} onClick={() => logCallStatus(cs)}>
                {cs}
              </Button>
            ))}
          </div>
        </div>
      )}
      {!ticketCfg && !task.closedAt && (
        <div className="td-block">
          <SectionTitle>{task.followUpAt ? 'Reschedule follow-up' : 'Set a follow-up'}</SectionTitle>
          <div className="td-followup">
            <Field label="Follow-up date & time" className="td-followup__field">
              {(control) => (
                <TextInput
                  {...control}
                  type="datetime-local"
                  size="sm"
                  value={followUpAt}
                  onChange={(e) => setFollowUpAt(e.target.value)}
                />
              )}
            </Field>
            <Button size="sm" variant="secondary" disabled={busy} onClick={() => saveFollowUp(false)}>
              {task.followUpAt ? 'Reschedule' : 'Set'}
            </Button>
          </div>
        </div>
      )}
      <div className="td-block">
        <SectionTitle>Add a note</SectionTitle>
        <div className="tp-note-row">
          <textarea
            className="ds-input tp-textarea"
            rows={2}
            aria-label="Note"
            placeholder={ticketCfg ? 'Add a note to the timeline' : 'Add a note (required for moves marked ✎)'}
            value={note}
            onChange={(e) => setNote(e.target.value)}
          />
          <Button
            size="sm"
            variant="secondary"
            className="tp-note-btn"
            icon={<FileText size={14} aria-hidden />}
            disabled={busy}
            onClick={comment}
          >
            Add note
          </Button>
        </div>
      </div>
    </>
  );

  /* ── Timeline ────────────────────────────────────────────────────────── */
  const timeline = (
    <>
      <SectionTitle
        right={
          <Segmented<TimelineFilter>
            size="sm"
            ariaLabel="Filter the timeline"
            value={timelineFilter}
            onChange={setTimelineFilter}
            options={TIMELINE_FILTERS.map((f) => ({
              value: f.key,
              label: `${f.label} (${task.events.filter(f.match).length})`,
            }))}
          />
        }
      >
        Activity timeline
      </SectionTitle>
      <ul className={`td-timeline${isPanel ? '' : ' td-timeline--scroll'}`}>
        {[...task.events]
          .reverse()
          .filter((e) => (TIMELINE_FILTERS.find((x) => x.key === timelineFilter)?.match ?? (() => true))(e))
          .map((e) => {
            // A dialer call's recording lives inline under its CALL_LINKED
            // entry (matched by telephonyCallId).
            const call =
              e.type === 'CALL_LINKED' && e.payload?.telephonyCallId
                ? task.calls.find((c) => c.telephonyCallId === String(e.payload?.telephonyCallId))
                : undefined;
            const chips = eventAnswers(e, ticketCfg);
            return (
              <li key={e.id} className="td-event">
                <div className="td-event__row">
                  {e.type === 'CALL_LINKED' ? (
                    <span className="td-event__call" aria-hidden="true">
                      <i className="fa fa-phone" />
                    </span>
                  ) : (
                    <i className={`fa ${EVENT_ICONS[e.type] || 'fa-circle-o'} td-event__icon`} aria-hidden="true" />
                  )}
                  <div className="td-event__body">
                    {editingNoteId === e.id ? (
                      <span className="td-event__edit">
                        <textarea
                          className="ds-input tp-textarea"
                          rows={2}
                          aria-label="Edit note"
                          value={noteDraft}
                          onChange={(ev) => setNoteDraft(ev.target.value)}
                        />
                        <Button size="sm" variant="primary" disabled={busy} onClick={() => saveNoteEdit(e.id)}>
                          Save
                        </Button>
                        <Button size="sm" variant="secondary" onClick={() => setEditingNoteId(null)}>
                          Cancel
                        </Button>
                      </span>
                    ) : (
                      <>
                        <span className="ds-strong">
                          {e.actorName || (e.actorType === 'SYSTEM' || e.actorType === 'API' ? 'System' : e.actorExternalId || e.actorType)}
                        </span>{' '}
                        — {eventLine(e)}
                        {e.note && e.type !== 'COMMENT' && e.type !== 'MIGRATED' && (
                          <div className="td-event__quote">“{e.note}”</div>
                        )}
                        {e.type === 'COMMENT' && (
                          <>
                            {e.payload?.editedAt && <span className="ds-text-muted"> (edited)</span>}
                            {e.actorExternalId && e.actorExternalId === me.externalId && (
                              <IconButton
                                className="td-icon-inline"
                                title="Edit note"
                                aria-label="Edit note"
                                onClick={() => {
                                  setEditingNoteId(e.id);
                                  setNoteDraft(e.note || '');
                                }}
                              >
                                <Edit2 size={12} aria-hidden />
                              </IconButton>
                            )}
                          </>
                        )}
                        {chips.length > 0 && (
                          <div className="td-event__chips">
                            {chips.map((a) => (
                              <span key={a} className="td-chip">
                                {a}
                              </span>
                            ))}
                          </div>
                        )}
                        <div className="rm-caption">{fmt(e.createdAt)}</div>
                      </>
                    )}
                  </div>
                </div>
                {call && (call.recordingAvailable || call.recordingId) && (
                  <div className="td-event__recording">
                    <RecordingCell call={call} />
                  </div>
                )}
              </li>
            );
          })}
      </ul>
    </>
  );

  const main = (
    <div className="td-main">
      {header}
      {showSummary && task.customerType === 'STUDENT' && (
        <AiSummaryCard studentExternalId={task.customerExternalId} />
      )}
      {facts}
      {contextPanel}
      {owner}
      {stage}
      {goal}
      {closedBlock}
      {ticketCfg && <CapturedAnswers cfg={ticketCfg} values={answers} />}
      {actions}
    </div>
  );

  return (
    <>
      {isPanel ? (
        <div className="td-panel">
          {main}
          <div className="td-panel__timeline">{timeline}</div>
        </div>
      ) : (
        <Row className="g-3">
          <Col xl={7}>
            <Card>{main}</Card>
          </Col>
          <Col xl={5}>
            <Card>{timeline}</Card>
          </Col>
        </Row>
      )}
      <StudentDrawer student={drawerStudent} onClose={() => setDrawerStudent(null)} />
    </>
  );
};

/**
 * Consumer Xperience → Task detail as a routed page (/pages/rm-tasks/:taskId)
 * — shareable and deep-linkable; the task list opens the same view in a side
 * panel.
 */
const TaskDetailPage: React.FC = () => {
  const { taskId = '' } = useParams<{ taskId: string }>();
  const navigate = useNavigate();

  const back = (
    <Button size="sm" variant="secondary" icon={<ArrowLeft size={14} aria-hidden />} onClick={() => navigate(-1)}>
      Back
    </Button>
  );

  if (!RM_TASKS_ENABLED) {
    return (
      <Page>
        <PageHeader eyebrow="RM Tasks" title="Task" actions={back} />
        <EmptyState title="Not enabled here" description="This feature isn't enabled in this environment yet." />
      </Page>
    );
  }

  return (
    <Page>
      <PageHeader
        eyebrow="RM Tasks"
        title="Task"
        subtitle="Work the ticket: log activity, close it, and see its timeline."
        actions={back}
      />
      <TaskDetailView taskId={taskId} variant="page" />
    </Page>
  );
};

export default TaskDetailPage;
